import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { FacturacionService, Factura } from '../../core/services/facturacion.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import { MetaService } from '../../core/services/meta.service';
import Swal from 'sweetalert2';

const RESEND_COOLDOWN_KEY = 'factura_resend_cooldown';
const RESEND_COOLDOWN_SECONDS = 60;

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, RouterModule, Header, Footer],
  templateUrl: './profile.html',
  styleUrls: ['./profile.css']
})
export class ProfileComponent implements OnInit, OnDestroy {
  authService = inject(AuthService);
  private router = inject(Router);
  private facturacionService = inject(FacturacionService);
  private metaService = inject(MetaService);

  facturas = signal<Factura[]>([]);
  selectedFactura = signal<Factura | null>(null);
  isLoadingFacturas = signal(false);
  isLoadingDetails = signal(false);
  errorMessage = signal<string | null>(null);
  showInvoiceDetail = signal(false);
  isResendingEmail = signal(false);
  resendCooldownRemaining = signal(0);

  private cooldownInterval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.metaService.resetToDefault();
    this.loadFacturas();
    this.checkResendCooldown();
  }

  ngOnDestroy(): void {
    this.clearCooldownInterval();
  }

  loadFacturas(): void {
    this.isLoadingFacturas.set(true);
    this.errorMessage.set(null);

    this.facturacionService.getMyInvoices().subscribe({
      next: (response) => {
        if (response.success) {
          this.facturas.set(response.data);
          console.log('✅ Facturas cargadas:', response.data);
        } else {
          this.errorMessage.set(response.message);
        }
        this.isLoadingFacturas.set(false);
      },
      error: (error) => {
        console.error('❌ Error al cargar facturas:', error);
        this.errorMessage.set('Error al cargar las facturas. Por favor, intenta nuevamente.');
        this.isLoadingFacturas.set(false);
      }
    });
  }

  viewInvoiceDetails(facturaid: string): void {
    this.isLoadingDetails.set(true);
    this.errorMessage.set(null);

    this.facturacionService.getInvoiceDetails(facturaid).subscribe({
      next: (response) => {
        if (response.success) {
          this.selectedFactura.set(response.data);
          this.showInvoiceDetail.set(true);
          console.log('✅ Detalle de factura cargado:', response.data);
        } else {
          this.errorMessage.set(response.message);
        }
        this.isLoadingDetails.set(false);
      },
      error: (error) => {
        console.error('❌ Error al cargar detalle de factura:', error);
        this.errorMessage.set('Error al cargar el detalle. Por favor, intenta nuevamente.');
        this.isLoadingDetails.set(false);
      }
    });
  }

  closeInvoiceDetail(): void {
    this.showInvoiceDetail.set(false);
    this.selectedFactura.set(null);
  }

  private checkResendCooldown(): void {
    const stored = sessionStorage.getItem(RESEND_COOLDOWN_KEY);
    if (!stored) {
      this.resendCooldownRemaining.set(0);
      return;
    }

    const sentAt = parseInt(stored, 10);
    const now = Date.now();
    const elapsed = Math.floor((now - sentAt) / 1000);
    const remaining = Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);

    this.resendCooldownRemaining.set(remaining);

    if (remaining > 0) {
      this.startCooldownInterval();
    } else {
      sessionStorage.removeItem(RESEND_COOLDOWN_KEY);
    }
  }

  private startCooldownInterval(): void {
    this.clearCooldownInterval();
    this.cooldownInterval = setInterval(() => {
      const current = this.resendCooldownRemaining();
      if (current <= 1) {
        this.resendCooldownRemaining.set(0);
        sessionStorage.removeItem(RESEND_COOLDOWN_KEY);
        this.clearCooldownInterval();
      } else {
        this.resendCooldownRemaining.set(current - 1);
      }
    }, 1000);
  }

  private clearCooldownInterval(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
      this.cooldownInterval = null;
    }
  }

  formatCooldownTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  printInvoice(): void {
    const facturaid = this.selectedFactura()?.facturaid;
    if (facturaid) {
      // Navegar a la página de impresión en una nueva pestaña
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/print-invoice', facturaid])
      );
      window.open(url, '_blank');
    }
  }

  resendEmail(): void {
    const facturaid = this.selectedFactura()?.facturaid;
    if (!facturaid || this.isResendingEmail() || this.resendCooldownRemaining() > 0) return;

    this.isResendingEmail.set(true);

    this.facturacionService.resendFacturaEmail(facturaid).subscribe({
      next: (response) => {
        if (response.success) {
          console.log('✅ Email reenviado exitosamente:', response.data);
          sessionStorage.setItem(RESEND_COOLDOWN_KEY, Date.now().toString());
          this.resendCooldownRemaining.set(RESEND_COOLDOWN_SECONDS);
          this.startCooldownInterval();
          Swal.fire({
            icon: 'success',
            title: 'Email enviado',
            text: 'El email de factura ha sido reenviado exitosamente.',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#22c55e'
          });
        } else {
          console.error('❌ Error al reenviar email:', response.message);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error al reenviar el email: ' + response.message,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#e8343f'
          });
        }
        this.isResendingEmail.set(false);
      },
      error: (error) => {
        console.error('❌ Error al reenviar email:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al reenviar el email. Por favor, intenta nuevamente.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#e8343f'
        });
        this.isResendingEmail.set(false);
      }
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Fecha no disponible';
    
    try {
      const date = new Date(dateString);
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      
      return date.toLocaleDateString('es-EC', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/Guayaquil'
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'Fecha inválida';
    }
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return 'Fecha no disponible';
    
    try {
      const date = new Date(dateString);
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      
      return date.toLocaleString('es-EC', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Guayaquil'
      });
    } catch (error) {
      console.error('Error al formatear fecha y hora:', error);
      return 'Fecha inválida';
    }
  }

  getEstadoBadgeClass(estado: string): string {
    const estadoLower = estado.toLowerCase();
    const classes: { [key: string]: string } = {
      'emitida': 'bg-green-100 text-green-800',
      'pagada': 'bg-green-100 text-green-800',
      'pendiente': 'bg-yellow-100 text-yellow-800',
      'cancelada': 'bg-red-100 text-red-800',
      'reembolsada': 'bg-gray-100 text-gray-800'
    };
    return classes[estadoLower] || 'bg-gray-100 text-gray-800';
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/']);
      },
      error: () => {
        this.authService.clearSession();
        this.router.navigate(['/']);
      }
    });
  }
}
